/**
 * @file order-chain.ts
 * @description Контракт агрегата «Путь заказа» и хук его загрузки
 * (phase19.24 этап 1, шаг 5.3; проект — phase19.24-order-card-chain-design.md
 * §9, §13, §14).
 *
 * Один серверный агрегат `GET /admin/orders/:id/chain` вместо 5–6 водопадных
 * запросов: порядок и состав узлов — бизнес-правило (маршрут F31, F30, B2B),
 * а не раскладка, и права на источники проверяет сервер (`hidden`).
 *
 * Типы зеркалят серверные DTO ИМЕНАМИ ПОЛЕЙ из проекта фазы — расхождение
 * ловится сразу, а не после первой загрузки. Всё, что источник может не
 * прислать (деградация склада, отсутствие прав), выражено состоянием узла, а
 * не отсутствием узла: карточка обязана показать «статус не загрузился», а не
 * тихо потерять строку.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, apiFetch } from "./api-client";
import { ORDER_STAGE_KIND_LABEL, type OrderStageKind } from "./api-orders";
import type { DocumentStatusTone } from "../components/ui/DocumentStatusBadge";
import type { OrderTabKey } from "../hooks/useOrderTabParam";

// ─── Маршрут (степпер = стадии) ─────────────────────────────────────────────

/** Чей сейчас ход. Вычисляется сервером, клиент только показывает (§14). */
export type OrderChainOwner =
  | "MANAGER"
  | "WAREHOUSE"
  | "CUSTOMER"
  | "CARRIER"
  | "SYSTEM";

/** Состояние точки степпера. */
export type OrderRouteStageState = "passed" | "current" | "future" | "skipped";

/** Проблема текущей стадии — красный стикер поверх владельца (§14). */
export type OrderChainProblem =
  | "SHORTAGE"
  | "AGREEMENT_OUTDATED"
  | "CARRIER_SILENT"
  /**
   * Позиция заказа без товара каталога (OQ-54): собрать её нельзя. Клиент
   * выводит проблему и сам — по составу заказа (`order.items`), — но значение
   * поддержано здесь: сервер начнёт присылать его вместе с защитной проверкой
   * проведения задания.
   */
  | "ITEM_UNLINKED";

/**
 * Вид точки маршрута. Кроме настоящих стадий заказа бывает заглушка
 * `HANDOVER_PENDING` — «Получение», когда способ получения ещё не выбран
 * (сервер, 2026-09-21): такой стадии в воронке нет, поэтому ЛЮБОЙ лукап по
 * `kind` обязан иметь фолбэк на `name`/`hint`, а не падать и не молчать.
 */
export type OrderRouteStageKind = OrderStageKind | "HANDOVER_PENDING";

export interface OrderRouteStageDto {
  /** Системный вид стадии: маршрут строим по нему, показываем `name`. */
  kind: OrderRouteStageKind;
  /** Имя стадии, как её назвал магазин. */
  name: string;
  /** Пояснение точки («Способ получения выберут при согласовании»). */
  hint?: string | null;
  state: OrderRouteStageState;
  owner: OrderChainOwner;
  /** Когда заказ вошёл в стадию (для «2 ч 15 мин»). */
  sinceAt: string | null;
  /** Срок стадии по F31 (24 ч карта / 5 дн счёт / резерв). */
  deadlineAt: string | null;
  /** Момент прохождения стадии — у пройденных. */
  at: string | null;
}

export interface OrderRouteCurrentDto {
  /**
   * Вид текущей стадии заказа. Тексты полосы «Сейчас» и главную кнопку берём
   * ПО НЕМУ, а не по поиску точки в шкале: шкала показывает маршрут (включая
   * заглушку «Получение»), а действует заказ по своей стадии.
   */
  kind?: OrderStageKind;
  /** С какого момента стадия просрочена; null — в срок. */
  overdueSince: string | null;
  problem: OrderChainProblem | null;
}

export interface OrderRouteDto {
  key: string;
  /** «доставка ТК, предоплата» — подпись рядом с заголовком блока. */
  label: string;
  stages: OrderRouteStageDto[];
  current: OrderRouteCurrentDto | null;
  /** «этап 4 из 9»; null — матрицы маршрута ещё нет, строку прячем. */
  currentIndex: number | null;
  totalCount: number | null;
}

// ─── Узлы (список = документы) ──────────────────────────────────────────────

/** Вид узла. `RECEIPT` — чек ККТ, серый до phase20 (решение §12 п.5). */
export type OrderChainNodeKey =
  | "ORDER"
  | "AGREEMENT"
  | "INVOICE"
  | "PAYMENT"
  | "PICKING"
  | "SHIPMENT"
  | "UPD"
  | "RETURN"
  | "DEAL"
  | "HANDOVER"
  | "RECEIPT";

export type OrderChainNodeState =
  | "done"
  | "current"
  | "future"
  | "failed"
  | "skipped"
  /** Источник не ответил: узел на месте, статус неизвестен (§8). */
  | "unavailable";

export interface OrderChainActorDto {
  displayName: string | null;
  kind: "USER" | "SYSTEM" | "CUSTOMER" | "CARRIER";
}

/** Вторая строка узла: прогресс, сумма, трек, срок действия. */
export interface OrderChainDetailDto {
  progress?: { done: number; total: number } | null;
  amount?: string | null;
  currency?: string | null;
  trackNumber?: string | null;
  expiresAt?: string | null;
  /** Способ оплаты словами: «наличные», «карта» (решение §12 п.1). */
  method?: string | null;
}

/**
 * Куда ведёт узел. `path` приходит без `/shop/:slug` — префикс ставит клиент
 * (`order-chain-links.ts`), сервер про слаг не знает.
 */
export type OrderChainLinkDto =
  | { kind: "route"; path: string }
  /**
   * `tab` — ключ из реестра вкладок карточки (`ORDER_TABS`). Тип узкий
   * намеренно: сервер и клиент обязаны звать вкладки одинаково, а ключ вне
   * реестра — расхождение контрактов, и ссылка в этом случае не строится
   * (`order-chain-links.ts`).
   */
  | { kind: "tab"; tab: OrderTabKey; focusId?: string | null }
  | { kind: "anchor"; anchor: string };

export interface OrderChainNodeDto {
  key: OrderChainNodeKey;
  title: string;
  number: string | null;
  statusLabel: string;
  statusTone: DocumentStatusTone;
  state: OrderChainNodeState;
  /** Момент, сделавший узел фактом (проведён / получен / вручён). */
  occurredAt: string | null;
  createdAt: string | null;
  actor: OrderChainActorDto | null;
  /** У будущего узла — обязателен: чем именно создастся. */
  hint: string | null;
  detail: OrderChainDetailDto | null;
  link: OrderChainLinkDto | null;
  /** Однотипные узлы с одним ключом группировки сворачиваются от 2 штук. */
  groupKey?: string | null;
  /**
   * Владелец документа — им красится рейл узла (§14). Считает сервер;
   * `undefined` (старый ответ) — клиент выводит владельца по исполнителю и
   * виду узла (`order-chain-node-view.ts`).
   */
  owner?: OrderChainOwner | null;
  /** Узел скрыт правами: номер не показываем, строку — показываем. */
  hidden?: "no_permission" | null;
}

export interface OrderChainDegradedDto {
  /** «wms», «crm», «sdek» — чей источник не ответил. */
  source: string;
  message: string;
}

export interface OrderChainDto {
  route: OrderRouteDto;
  nodes: OrderChainNodeDto[];
  degraded: OrderChainDegradedDto[];
}

// ─── Загрузка ───────────────────────────────────────────────────────────────

export interface OrderChainState {
  data: OrderChainDto | null;
  loading: boolean;
  /** Человеческий текст ошибки для полосы «Не удалось собрать путь». */
  error: string | null;
  /** Повторная попытка — кнопка «Повторить» в полосе и в узле. */
  reload: () => void;
}

/**
 * Загрузка цепочки заказа. Ошибка агрегата не роняет карточку: страница уже
 * отрисована по `GET /admin/orders/:id`, а блок «Путь» показывает полосу с
 * «Повторить» (§8).
 */
export function useOrderChain(orderId: string | undefined): OrderChainState {
  const [data, setData] = useState<OrderChainDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Счётчик попыток: смена значения перезапускает эффект. */
  const [attempt, setAttempt] = useState(0);
  /** Гонка ответов: показываем результат только последнего запроса. */
  const requestId = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    const id = ++requestId.current;
    let cancelled = false;
    setLoading(true);
    apiFetch<OrderChainDto>(`/admin/orders/${orderId}/chain`)
      .then((res) => {
        if (cancelled || id !== requestId.current) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || id !== requestId.current) return;
        setError(
          err instanceof ApiError ? err.message : "Не удалось собрать путь заказа",
        );
      })
      .finally(() => {
        if (!cancelled && id === requestId.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { data, loading, error, reload };
}

/** Текущая стадия маршрута; null — маршрут пройден или пуст. */
export function currentStage(route: OrderRouteDto | null): OrderRouteStageDto | null {
  return route?.stages.find((s) => s.state === "current") ?? null;
}

/**
 * Ближайшая будущая стадия, где ход снова за менеджером («ваш след. шаг»).
 * Ищем строго после текущей: пройденные «мои» стадии уже не подсказка.
 */
export function nextManagerStage(
  route: OrderRouteDto | null,
): OrderRouteStageDto | null {
  if (!route) return null;
  const from = route.stages.findIndex((s) => s.state === "current");
  return (
    route.stages
      .slice(from + 1)
      .find((s) => s.owner === "MANAGER" && s.state === "future") ?? null
  );
}

/**
 * Настоящая стадия заказа или заглушка шкалы? Сравниваем с реестром подписей
 * (`ORDER_STAGE_KIND_LABEL`) — он и есть закрытый список видов стадий.
 */
export function isKnownStageKind(
  kind: OrderRouteStageKind | null | undefined,
): kind is OrderStageKind {
  return !!kind && kind in ORDER_STAGE_KIND_LABEL;
}

/**
 * Подпись точки шкалы: имя из воронки магазина, иначе системная метка вида.
 * У заглушки маршрута имени всегда хватает — но пустую строку тоже переживаем
 * без «undefined» в интерфейсе.
 */
export function routeStageLabel(stage: OrderRouteStageDto): string {
  const name = stage.name?.trim();
  if (name) return name;
  return isKnownStageKind(stage.kind) ? ORDER_STAGE_KIND_LABEL[stage.kind] : "Стадия";
}

/**
 * Вид текущей стадии для текстов и кнопки: сначала то, что прислал сервер
 * (`route.current.kind`), потом — вид самой точки, если он настоящий. У
 * заглушки «Получение» вида нет, и тексты берутся из `name`/`hint`.
 */
export function currentStageKind(
  route: OrderRouteDto | null,
  stage: OrderRouteStageDto | null,
): OrderStageKind | null {
  if (isKnownStageKind(route?.current?.kind)) return route.current.kind;
  return isKnownStageKind(stage?.kind) ? stage.kind : null;
}

/** Самовывоз определяем по составу маршрута, а не по угадыванию по `key`. */
export function isPickupRoute(route: OrderRouteDto | null): boolean {
  return !!route?.stages.some((s) => s.kind === "READY_FOR_PICKUP");
}

/**
 * Способ получения не выбран: сервер отдаёт ветки `UNKNOWN_PREPAY`,
 * `UNKNOWN_POSTPAY`, `B2B_UNKNOWN` (реестр `ORDER_ROUTE_BRANCHES`). Такой
 * заказ нельзя вести к оплате: стоимость доставки ещё изменит сумму
 * (решение пользователя 2026-09-21). Подпись ветки проверяем вторым
 * признаком — на случай, если сервер переименует ключ.
 */
export function isDeliveryUnknownRoute(route: OrderRouteDto | null): boolean {
  if (!route) return false;
  return (
    route.key.startsWith("UNKNOWN_") ||
    route.key === "B2B_UNKNOWN" ||
    route.label.includes("способ получения не указан")
  );
}

/**
 * Ветки «везём своими силами» (F18) — зеркало серверного реестра
 * `SHOP_COURIER_BRANCHES` (`chain/order-route-owner.ts`). Реестр, а не
 * `key.startsWith("SHOP_COURIER")`: строковая проверка развалилась бы на
 * B2B-ветке, у которой другой префикс.
 */
const SHOP_COURIER_ROUTE_KEYS: readonly string[] = [
  "SHOP_COURIER_PREPAY",
  "SHOP_COURIER_POSTPAY",
  "B2B_SHOP_COURIER",
];

/**
 * Заказ везёт курьер МАГАЗИНА: вручает менеджер, отгрузки и перевозчика нет.
 * Маршрут первичен, сводка `delivery.kind` — запасной признак (у старого
 * ответа сервера ветки ещё нет).
 */
export function isShopCourierRoute(
  route: OrderRouteDto | null,
  deliveryKind?: string | null,
): boolean {
  if (route && SHOP_COURIER_ROUTE_KEYS.includes(route.key)) return true;
  return deliveryKind === "COURIER";
}

/** Ветка «доставка по договорённости» (F30): согласование — часть маршрута. */
export function isAgreementRoute(route: OrderRouteDto | null): boolean {
  return (
    !!route &&
    (route.key.startsWith("AGREEMENT_") ||
      route.stages.some((s) => s.kind === "PENDING_DELIVERY_AGREEMENT"))
  );
}
